import { BOAT_PATH, pointAlongPath } from "./game.ts";
import type { CalibratedPoint, HeadInput } from "./tracking.ts";

export const PREMIUM_GAME_IDS = ["lighthouse", "cloud-trail", "star-path"] as const;
export type PremiumGameId = typeof PREMIUM_GAME_IDS[number];

const ACTIVE_DURATION_MS = 240_000;
const REST_DURATION_MS = 15_000;
const REST_AT_MS = [80_000, 160_000] as const;

export type PremiumJourneyState = {
  activeMs: number;
  restRemainingMs: number;
  nextRestIndex: number;
  complete: boolean;
};

export function initialPremiumJourney(): PremiumJourneyState {
  return { activeMs: 0, restRemainingMs: 0, nextRestIndex: 0, complete: false };
}

export function advancePremiumJourney(current: PremiumJourneyState, elapsedMs: number, canAdvance: boolean): PremiumJourneyState {
  if (current.complete || elapsedMs <= 0 || (current.restRemainingMs <= 0 && !canAdvance)) return current;
  let state = { ...current };
  let remaining = Math.max(0, elapsedMs);
  while (remaining > 0 && !state.complete) {
    if (state.restRemainingMs > 0) {
      const consumed = Math.min(remaining, state.restRemainingMs);
      state.restRemainingMs -= consumed;
      remaining -= consumed;
      continue;
    }
    if (!canAdvance) break;
    const nextBoundary = REST_AT_MS[state.nextRestIndex] ?? ACTIVE_DURATION_MS;
    const consumed = Math.min(remaining, nextBoundary - state.activeMs);
    state.activeMs += consumed;
    remaining -= consumed;
    if (state.activeMs >= ACTIVE_DURATION_MS) {
      state.activeMs = ACTIVE_DURATION_MS;
      state.complete = true;
    } else if (state.activeMs >= nextBoundary) {
      state.nextRestIndex += 1;
      state.restRemainingMs = REST_DURATION_MS;
    }
  }
  return state;
}

const STAR_TARGETS: CalibratedPoint[] = [
  { x: -0.67, y: -0.08 }, { x: 0.67, y: -0.08 }, { x: 0, y: -0.64 },
  { x: -0.48, y: 0.44 }, { x: 0.48, y: 0.44 }, { x: 0.46, y: -0.46 },
];

export function premiumGuidePoint(game: PremiumGameId, activeMs: number): CalibratedPoint {
  const elapsed = Math.max(0, activeMs);
  if (game === "lighthouse") return { x: Math.sin((elapsed / 24_000) * Math.PI * 2) * 0.68, y: 0 };
  if (game === "cloud-trail") return pointAlongPath(BOAT_PATH, (elapsed % 60_000) / 60_000);
  const cycle = Math.floor(elapsed / 12_000);
  const phase = (elapsed % 12_000) / 12_000;
  const target = STAR_TARGETS[cycle % STAR_TARGETS.length];
  const scale = phase < 0.25 ? phase / 0.25
    : phase < 0.45 ? 1
      : phase < 0.75 ? 1 - (phase - 0.45) / 0.3
        : 0;
  return { x: target.x * scale, y: target.y * scale };
}

export function playablePoint(input: HeadInput | null): CalibratedPoint | null {
  return input?.isTracked && !input.isTooFast ? { x: input.x, y: input.y } : null;
}
